import * as React from "react";
import {createRoot, type Root} from "react-dom/client";
import type {FieldError} from "@/shared/timr";
import type {DurationUnit} from "@/shared/timrTypes";
import {WorkingTimeRangePanel} from "@/shared/ui/workingTimeRange/WorkingTimeRangePanel";
import type {WorkingTimeRangeValue} from "@/shared/ui/workingTimeRange/workingTimeRangeState";
import {Temporal} from "@/shared/utils/datefunctions";

export type {WorkingTimeRangeValue};

export interface WorkingTimeRangePanelBridgeConfig {
	/** DOM node the React panel is mounted into; the bridge throws if it is missing. */
	container: HTMLElement | null;
	/** Notified with the next value after every user edit. */
	onChange: (value: WorkingTimeRangeValue) => void;
	/** Label for the start-date field (day mode). */
	startLabel: string;
	/** Label for the end-date field (day mode). */
	endLabel: string;
	/** Whether the break-time field is editable (minute mode, independent of the date/time fields). */
	breakTimeEnabled: boolean;
	/** The most recent record's end for the start field's "shift to last end" shortcut (minute mode); omit to hide it. */
	lastRecordEnd?: Temporal.ZonedDateTime | null;
	/** Reveal the timezone fields when the seeded offsets differ from the current zone; set when duplicating a record. */
	forceTimeZoneFields?: boolean;
	/** Re-anchor a calculated endpoint's offset to its local date instead of the persisted source offset; set when duplicating a record. */
	localizeCalculatedOffset?: boolean;
}

// TODO: replace this request-specific bridge with the shared ReactIsland bridge primitive.
export class WorkingTimeRangePanelBridge {
	private readonly root: Root;
	private destroyed = false;

	constructor(private readonly config: WorkingTimeRangePanelBridgeConfig) {
		if (!config.container) {
			throw new Error("Working time range panel container is missing");
		}
		this.root = createRoot(config.container, {
			identifierPrefix: "working-time-range-panel-"
		});
	}

	/**
	 * Re-renders the controlled panel with the value/edit-unit/errors. Passing a NEW `value` object re-seeds
	 * the panel and discards in-progress edit state; to change `editUnit`/`disabled`/`fieldErrors` without
	 * re-seeding, pass the same object again — e.g. the latest one received via
	 * {@link WorkingTimeRangePanelBridgeConfig.onChange}.
	 */
	public update(value: WorkingTimeRangeValue, editUnit: DurationUnit, disabled: boolean, fieldErrors: FieldError[] = []) {
		if (this.destroyed) return;
		this.root.render(
			<WorkingTimeRangePanel
				value={value}
				editUnit={editUnit}
				fieldErrors={fieldErrors}
				disabled={disabled}
				onValueChange={this.config.onChange}
				startLabel={this.config.startLabel}
				endLabel={this.config.endLabel}
				breakTimeEditable={this.config.breakTimeEnabled}
				lastRecordEnd={this.config.lastRecordEnd}
				forceTimeZoneFields={this.config.forceTimeZoneFields}
				localizeCalculatedOffset={this.config.localizeCalculatedOffset}
			/>
		);
	}

	public destroy() {
		if (this.destroyed) return;
		this.root.unmount();
		this.destroyed = true;
	}
}
